import { compact, isEmpty } from 'lodash';
import { useCallback } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';

import {
  ExtendedTypeaheadQueryOpts,
  useAsyncTypeaheadProps,
} from '@/components/form/baseInputs/BackendTypeaheadSelectInput/hooks/useAsyncTypeaheadProps';
import { FormAwareTypeaheadMultiSelectInputProps } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';

import {
  GetTeamsMultipleQuery,
  GetTeamsMultipleQueryVariables,
  useGetTeamsMultipleQuery,
} from '../graphql/TeamsTypeahead.generated';

type TeamsTypeaheadProps<FormShape extends FieldValues> = Omit<
  FormAwareTypeaheadMultiSelectInputProps<FormShape>,
  'options' | 'inputValue' | 'onInputChange'
>;

export function useTeamsTypeahead<FormShape extends FieldValues>(
  fieldName: TeamsTypeaheadProps<FormShape>['fieldName'],
  disableSCIMManagedTeams: boolean
) {
  const { createErrorFeedback } = useFeedback();
  const { control } = useFormContext<FormShape>();

  // Watch currently-selected ids so we can ask the backend for them
  const selectedIds = useWatch({ control, name: fieldName }) as string[];

  // Map the user's search text => GraphQL variables for queryData
  const searchTermToVariables = useCallback(
    (search: string) => ({
      query: search,
    }),
    []
  );

  // Always ask for the full records of already-selected items
  const getSelectedOptionVariables = useCallback(
    () => ({
      selectedOptionsLike: {
        idIn: isEmpty(selectedIds) ? [''] : compact(selectedIds),
      },
    }),
    [selectedIds]
  );

  const toOptions: ExtendedTypeaheadQueryOpts<
    string,
    GetTeamsMultipleQuery,
    GetTeamsMultipleQueryVariables
  >['toOptions'] = useCallback(
    (rows) =>
      rows.map((team) => ({
        value: team.id,
        display: team.workosDirectoryGroupID
          ? `${team.name} (SCIM)`
          : team.name,
        disabled: disableSCIMManagedTeams && !!team.workosDirectoryGroupID,
      })),
    [disableSCIMManagedTeams]
  );

  const [typeaheadProps, queryResult] = useAsyncTypeaheadProps(
    useGetTeamsMultipleQuery,
    {
      searchTermToVariables,
      getSelectedOptionVariables,
      toOptions,
      pageSize: 50,
      onError: createErrorFeedback(
        'Failed to fetch teams. Please refresh the page to try again.'
      ),
      unselectedGroupLabel: 'Available teams',
      skipAutocompleteResetClear: true,
    }
  );

  return { typeaheadProps, queryResult };
}
